/**
 * "WhatsApp Order Received" trigger — one node, one event dropdown, same
 * pattern as the Shopify Trigger node (shopifyTriggerEvents.ts): a new order
 * (order.placed) plus the payment events fired by the backend's
 * OrderPaymentService (`whatsapp.*`). Their own trigger types, so these flows
 * never run for Shopify/WooCommerce orders, and the Shopify trigger's
 * "Order Paid" / "Refund Created" never run for WhatsApp orders.
 */
export interface WhatsAppOrderVariableField {
  path: string;
  label: string;
}

export interface WhatsAppOrderTriggerEvent {
  id: string;
  label: string;
  description: string;
  triggerType: string;
  variableFields: WhatsAppOrderVariableField[];
  /** Left out of the event dropdown; flows already saved with it still work. */
  hidden?: boolean;
}

export const WHATSAPP_ORDER_RECEIVED_TRIGGER_TYPE = 'whatsapp_order_received';

const PAYMENT_EVENT_FIELDS: WhatsAppOrderVariableField[] = [
  { path: 'orderDetails.order_number', label: 'Order Number' },
  { path: 'orderDetails.total_amount', label: 'Order Total' },
  { path: 'orderDetails.currency', label: 'Order Currency' },
  { path: 'orderDetails.payment_status', label: 'Payment Status' },
  { path: 'orderDetails.amount', label: 'Amount' },
  { path: 'orderDetails.reference', label: 'Payment Reference' },
  { path: 'orderDetails.customer.first_name', label: 'Customer Name' },
  { path: 'orderDetails.customer.phone', label: 'Customer Phone' },
];

const BASE_FIELD: WhatsAppOrderVariableField = { path: '', label: '(Formatted)' };

const ORDER_RECEIVED_FIELDS: WhatsAppOrderVariableField[] = [
  BASE_FIELD,
  { path: 'orderDetails.name', label: 'Order Name' },
  { path: 'orderDetails.id', label: 'Order ID' },
  { path: 'orderDetails.total_price', label: 'Order Price' },
  { path: 'orderDetails.currency', label: 'Order Currency' },
  { path: 'orderDetails.status', label: 'Order Status' },
  { path: 'orderDetails.financial_status', label: 'Payment Status' },
  { path: 'orderDetails.fulfillment_status', label: 'Fulfillment Status' },
  { path: 'orderDetails.note', label: 'Order Note' },
  { path: 'orderDetails.order_status_url', label: 'Order Link' },
  { path: 'orderDetails.product_names', label: 'Product Names (Pizza, Coke, Fries)' },
  { path: 'orderDetails.product_list', label: 'Product List with Qty (Pizza x2, Coke x1)' },
  { path: 'orderDetails.item_count', label: 'Item Count' },
  { path: 'orderDetails.customer.first_name', label: 'Customer First Name' },
  { path: 'orderDetails.customer.last_name', label: 'Customer Last Name' },
  { path: 'orderDetails.email', label: 'Customer Email' },
  { path: 'orderDetails.customer.phone', label: 'Customer Phone' },
  // Where the cart is from, and — for a Shopify / WooCommerce cart — the store's
  // checkout that WeNext created for it.
  { path: 'orderDetails.cart_source', label: 'Cart Source (native / shopify / woocommerce)' },
  { path: 'orderDetails.checkout_url', label: 'Store Cart: Pay Link (full URL)' },
  { path: 'orderDetails.store_order_name', label: 'Store Cart: Store Order Name (#D1001)' },
  { path: 'orderDetails.shop', label: 'Store Cart: Store' },
];

export const WHATSAPP_ORDER_TRIGGER_EVENTS: WhatsAppOrderTriggerEvent[] = [
  {
    id: 'order_received',
    label: 'Order Received',
    description: 'Runs when a customer completes a new WhatsApp catalog order.',
    triggerType: 'order.placed',
    variableFields: ORDER_RECEIVED_FIELDS,
  },
  {
    id: 'order_paid',
    label: 'Order Paid',
    description: 'Runs when a WhatsApp order is paid in full (payment link, WhatsApp Pay, or marked paid).',
    triggerType: 'whatsapp.order.paid',
    variableFields: PAYMENT_EVENT_FIELDS,
    hidden: true,
  },
  {
    id: 'payment_failed',
    label: 'Payment Failed',
    description:
      'Runs when a payment attempt on a WhatsApp order fails, or its payment link is cancelled or expires unpaid.',
    triggerType: 'whatsapp.order.payment_failed',
    variableFields: PAYMENT_EVENT_FIELDS,
    hidden: true,
  },
  {
    id: 'refund_created',
    label: 'Refund Created',
    description: 'Runs when a WhatsApp order is refunded (fully or partly).',
    triggerType: 'whatsapp.refund.created',
    variableFields: PAYMENT_EVENT_FIELDS,
    hidden: true,
  },
];

/** The events offered in the node's dropdown. Only Order Received for now; the payment events are hidden. */
export const SELECTABLE_WHATSAPP_ORDER_TRIGGER_EVENTS = WHATSAPP_ORDER_TRIGGER_EVENTS.filter((e) => !e.hidden);

export const DEFAULT_WHATSAPP_ORDER_EVENT_ID = 'order_received';

export function getWhatsAppOrderEvent(eventId?: string | null): WhatsAppOrderTriggerEvent {
  return (
    WHATSAPP_ORDER_TRIGGER_EVENTS.find((e) => e.id === eventId) ??
    WHATSAPP_ORDER_TRIGGER_EVENTS.find((e) => e.id === DEFAULT_WHATSAPP_ORDER_EVENT_ID)!
  );
}

export function whatsappOrderEventIdFromNodeData(data?: Record<string, unknown> | null): string | undefined {
  const nested = data?.config as Record<string, unknown> | undefined;
  return (data?.whatsappOrderEvent ?? nested?.whatsappOrderEvent) as string | undefined;
}

export function whatsappOrderTriggerTypeFor(eventId?: string | null): string {
  return getWhatsAppOrderEvent(eventId).triggerType;
}
